import { useInfiniteQuery } from "@tanstack/react-query"
import { type CommentableEntity, fetchComments } from "../services/comment.api"
import { useCommentRealtime } from "./useCommentRealtime"

interface UseCommentsOptions {
  entityType: CommentableEntity
  entityId: string
  search?: string
  sort?: "newest" | "oldest" | "pinned"
  enabled?: boolean
}

const PAGE_SIZE = 20

export function useComments(options: UseCommentsOptions) {
  useCommentRealtime({
    entityType: options.entityType,
    entityId: options.entityId,
    enabled: options.enabled,
  })

  const query = useInfiniteQuery({
    queryKey: ["comments", options.entityType, options.entityId, options.search, options.sort],
    queryFn: ({ pageParam = 0 }) =>
      fetchComments(options.entityType, options.entityId, {
        limit: PAGE_SIZE,
        offset: pageParam as number,
        search: options.search || undefined,
        sort: options.sort || "pinned",
      }),
    initialPageParam: 0,
    getNextPageParam: lastPage =>
      lastPage.pagination.hasMore
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
    enabled: (options.enabled ?? true) && Boolean(options.entityId),
    staleTime: 1000 * 60 * 2,
  })

  const comments = query.data?.pages.flatMap(page => page.data) || []
  const total = query.data?.pages[0]?.pagination.total || 0

  return {
    comments,
    total,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
